import {
  formatSponsorPresentmentCurrency,
  isSponsorPresentmentCurrency,
  isSponsorPresentmentRateExact,
  listSponsorPresentmentCurrencies,
  quoteSponsorPriceFromXof,
  SPONSOR_PRESENTMENT_FX_SNAPSHOT_DATE,
  XOF_PER_EUR,
  type SponsorCurrencyRegion,
  type SponsorPresentmentCurrency,
} from '@washed/shared/money/sponsor-presentment-currency';

export const FX_SNAPSHOT_DATE = SPONSOR_PRESENTMENT_FX_SNAPSHOT_DATE;
export { XOF_PER_EUR };
export type CurrencyRegion = SponsorCurrencyRegion;
export type CurrencyMeta = SponsorPresentmentCurrency;

export const DEFAULT_CURRENCY = 'USD';

// ISO-3166 region → currency for the currencies we support. Auto-detection maps
// the payer's locale region onto this; anything unmapped falls back by language,
// then to USD.
const REGION_CURRENCY: Record<string, string> = {
  // Euro zone
  FR: 'EUR',
  BE: 'EUR',
  DE: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  PT: 'EUR',
  NL: 'EUR',
  IE: 'EUR',
  LU: 'EUR',
  AT: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  US: 'USD',
  GB: 'GBP',
  CA: 'CAD',
  CH: 'CHF',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
  AU: 'AUD',
  NZ: 'NZD',
  JP: 'JPY',
  CN: 'CNY',
  IN: 'INR',
  AE: 'AED',
  SA: 'SAR',
  QA: 'QAR',
  NG: 'NGN',
  GH: 'GHS',
  ZA: 'ZAR',
  KE: 'KES',
  MA: 'MAD',
  EG: 'EGP',
  BR: 'BRL',
  MX: 'MXN',
  // CFA zone — sponsor is local; keep them in XOF
  TG: 'XOF',
  CI: 'XOF',
  SN: 'XOF',
  BJ: 'XOF',
  BF: 'XOF',
  ML: 'XOF',
  NE: 'XOF',
  CM: 'XOF',
  GA: 'XOF',
};

const LANGUAGE_FALLBACK: Record<string, string> = {
  fr: 'EUR',
  en: 'USD',
  de: 'EUR',
  es: 'EUR',
  pt: 'EUR',
  ja: 'JPY',
  zh: 'CNY',
};

export function listCurrencies(): readonly CurrencyMeta[] {
  return listSponsorPresentmentCurrencies();
}

export function isSupportedCurrency(code: string): boolean {
  return isSponsorPresentmentCurrency(code);
}

export function currencyMeta(code: string): CurrencyMeta | undefined {
  return listSponsorPresentmentCurrencies().find((currency) => currency.code === code);
}

export function isExactRate(code: string): boolean {
  return isSponsorPresentmentCurrency(code) && isSponsorPresentmentRateExact(code);
}

/**
 * Best-effort currency for a sponsor, from an Intl-style locale tag
 * (e.g. "fr-FR", "en-GB"). Region subtag wins; then language; then USD.
 */
export function detectCurrency(locale: string | undefined): string {
  if (!locale) return DEFAULT_CURRENCY;
  const parts = locale.replace('_', '-').split('-');
  const language = (parts[0] ?? '').toLowerCase();
  const region = parts.find((p) => /^[A-Za-z]{2}$/.test(p) && p === p.toUpperCase());

  if (region && REGION_CURRENCY[region] && isSupportedCurrency(REGION_CURRENCY[region])) {
    return REGION_CURRENCY[region];
  }
  if (LANGUAGE_FALLBACK[language] && isSupportedCurrency(LANGUAGE_FALLBACK[language])) {
    return LANGUAGE_FALLBACK[language];
  }
  return DEFAULT_CURRENCY;
}

/** Detect from the browser, SSR-safe. */
export function detectBrowserCurrency(): string {
  if (typeof navigator === 'undefined') return DEFAULT_CURRENCY;
  return detectCurrency(navigator.language);
}

/** Convert an XOF amount (whole XOF — XOF is zero-decimal) to currency major units. */
export function convertFromXof(xofAmount: number, code: string): number {
  const meta = currencyMeta(code);
  if (!meta) return xofAmount;
  return xofAmount / meta.xofPerUnit;
}

export interface MoneyQuote {
  readonly code: string;
  /** Numeric value in major units of `code`. */
  readonly amount: number;
  /** Localised currency string, e.g. "31,71 €". */
  readonly formatted: string;
  /** False for the euro peg; true for snapshot estimates. */
  readonly estimate: boolean;
}

/**
 * The single seam between the UI and the money layer. Today it computes from the
 * snapshot/peg; swap the body for a server quote later and callers don't change.
 */
export function quoteFromXof(xofAmount: number, code: string, locale: string): MoneyQuote {
  const quote = quoteSponsorPriceFromXof(
    xofAmount,
    isSponsorPresentmentCurrency(code) ? code : 'XOF',
  );

  return {
    code: quote.code,
    amount: quote.amount,
    formatted: formatCurrency(quote.amount, quote.code, locale),
    estimate: quote.estimate,
  };
}

export function formatCurrency(amount: number, code: string, locale: string): string {
  if (isSponsorPresentmentCurrency(code)) {
    return formatSponsorPresentmentCurrency(amount, code, locale);
  }

  return `${new Intl.NumberFormat(locale).format(Math.round(amount))} ${code}`;
}
